/**
 * Jimi IoT adapter — IOT-VENDOR-JIMI-1.
 *
 * Container/asset tracker integration via Jimi Open Platform webhooks.
 * Supports: JM-LL301G (container tracking), JM-LL01 (asset tracking).
 * Capability surface: GPS position, temperature (cold chain), shock, battery SoC, geofence.
 *
 * Integration level: STUB — telemetry ingestion only.
 * Config push and OTA require Jimi Open Platform credentials (deferred to IOT-VENDOR-JIMI-2).
 * Geofence alerting is cloud-side (Kairos-computed, not onboard).
 *
 * HAL-WRITE-PATH-DOCTRINE-1: adapters do NOT write telemetry directly.
 * All ingest goes through iotIngest CF → this adapter's ingestReading().
 */

import { NotImplementedAdapter } from '../NotImplementedAdapter.js';
import { VendorNotIntegratedError } from '../VendorNotIntegratedError.js';

const JIMI_CAPABILITIES = {
  configKeys: {
    reporting_interval_sec: {
      pushable: true,
      readable: true,
      range: [60, 3600],
      requiresActivation: false,
      unit: 'seconds',
      description: 'Position and telemetry reporting interval',
    },
    temp_alert_max_c: {
      pushable: true,
      readable: true,
      range: [-40, 80],
      requiresActivation: false,
      unit: '°C',
      description: 'Temperature alert threshold (max)',
    },
    temp_alert_min_c: {
      pushable: true,
      readable: true,
      range: [-40, 80],
      requiresActivation: false,
      unit: '°C',
      description: 'Temperature alert threshold (min)',
    },
    geofence_enabled: {
      pushable: true,
      readable: true,
      enum: ['true', 'false'],
      requiresActivation: false,
      description: 'Enable cloud-side geofence alerting',
    },
  },
  telemetryTypes: [
    'gps_position',
    'temperature_c',
    'shock_g',
    'battery_soc_pct',
    'cell_signal_dbm',
  ],
  alertingMode: 'cloud',
  otaSupported: false,
  attestationSupported: false,
  rollbackSupported: false,
  pushLatencyExpectedMs: 30_000,
};

export class JimiAdapter extends NotImplementedAdapter {
  /**
   * @param {import('../../contracts/DeviceContract.ts').DeviceRecord} device
   */
  constructor(device) {
    super(device, 'jimi', JIMI_CAPABILITIES);

    /** @type {import('../../contracts/DeviceContract.ts').NormalizedReading[]} */
    this._readings = [];
    /** @type {Array<import('../../contracts/DeviceContract.ts').TelemetryHandler>} */
    this._telemetrySubscribers = [];
  }

  // ── Telemetry — STUB implementation ─────────────────────────────────────────

  /**
   * Feed a normalized reading into the adapter (called by iotIngest CF).
   * Notifies all live subscribers.
   *
   * @param {import('../../contracts/DeviceContract.ts').NormalizedReading} reading
   */
  ingestReading(reading) {
    this._readings.push(reading);
    for (const handler of this._telemetrySubscribers) {
      try { handler(reading); } catch { /* subscriber errors must not crash ingest */ }
    }
  }

  /**
   * Return readings for the requested time window and sensor types.
   *
   * @param {import('../../contracts/DeviceContract.ts').TimeWindow} window
   * @param {string[]} [sensorTypes]
   */
  async getTelemetry(window, sensorTypes) {
    let readings = this._readings;
    if (window) {
      const from = new Date(window.from).getTime();
      const to   = new Date(window.to).getTime();
      readings = readings.filter((r) => {
        const ts = new Date(r.timestamp).getTime();
        return ts >= from && ts <= to;
      });
    }
    if (sensorTypes?.length) {
      readings = readings.filter((r) => sensorTypes.includes(r.sensorType));
    }
    return readings;
  }

  subscribeToTelemetry(handler) {
    this._telemetrySubscribers.push(handler);
    return {
      unsubscribe: () => {
        this._telemetrySubscribers = this._telemetrySubscribers.filter((h) => h !== handler);
      },
    };
  }

  // ── Config / OTA — not integrated; explicit errors per STUB level ────────────

  async pushConfig()       { throw new VendorNotIntegratedError('jimi', 'pushConfig'); }
  async getCurrentConfig() { throw new VendorNotIntegratedError('jimi', 'getCurrentConfig'); }
  async getPendingConfig() { throw new VendorNotIntegratedError('jimi', 'getPendingConfig'); }
  async rollbackConfig()   { throw new VendorNotIntegratedError('jimi', 'rollbackConfig'); }
  async getDeviceHealth()  { throw new VendorNotIntegratedError('jimi', 'getDeviceHealth'); }
  subscribeToEvents()      { throw new VendorNotIntegratedError('jimi', 'subscribeToEvents'); }
}

/**
 * Factory for HalService.registerAdapter('jimi', createJimiAdapterFactory()).
 */
export function createJimiAdapterFactory() {
  return (device) => new JimiAdapter(device);
}
